import React, { useState } from 'react';
import type { VideoRef } from 'react-native-video';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import { radius, spacing } from '../theme';
import { useTranslation } from '../i18n/I18nContext';

/**
 * `react-native-video` NATIVE bir modül ve ikiliye ancak derlemeyle girer.
 * Güncelleme (OTA) yalnız JS taşıyor, yani bu dosya modülü taşımayan ESKİ bir
 * ikiliye de düşebiliyor — 14.09.2026 Android derlemesi tam olarak öyle: benim
 * yayınladığım kanalda ama içinde video modülü yok. Üst seviye `import`
 * bırakılsaydı o ikilide mesaj listesi bozulurdu.
 *
 * Aynı gerekçe `voice/recorder.ts` ve `media/captureLocation.ts` için de
 * geçerliydi; üçüncü bir desen açılmıyor.
 */
type VideoBilesen = React.ComponentType<Record<string, unknown>>;

function yukle(): VideoBilesen | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return (require('react-native-video').default ?? null) as VideoBilesen | null;
  } catch {
    return null;
  }
}

const NativeVideo = yukle();

/** 9.35.3 · D-830 — the same lazily required player for the profile photo viewer. */
export const NativeVideoView = NativeVideo;

export const videoPlaybackAvailable = NativeVideo != null;

/** Fotoğrafla aynı genişlik: sohbette iki medya türü aynı sütunda hizalanıyor. */
export const MSG_VIDEO_WIDTH = 220;
const MSG_VIDEO_HEIGHT = Math.round(MSG_VIDEO_WIDTH * 1.25);

export function formatVideoDuration(seconds: number | null | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  const tam = Math.round(seconds);
  const dakika = Math.floor(tam / 60);
  const saniye = tam % 60;
  return `${dakika}:${String(saniye).padStart(2, '0')}`;
}

type Props = {
  url: string;
  mine: boolean;
  durationSeconds?: number | null;
  onLongPress: () => void;
};

/**
 * Sohbetteki video (9.2.5 · D-563).
 *
 * **Kendiliğinden oynamıyor** ve bu bir tercih değil, tavanın sonucu: 1 GB'a
 * kadar dosya kabul ediyoruz ve listeyi kaydırmak birkaç videoyu birden
 * indirmeye başlatırdı — kullanıcının mobil verisini sormadan harcamak olurdu.
 * Dokunana kadar yalnız ilk kare gösteriliyor.
 *
 * Köşeler D-561'in kuralını izliyor: kavis balona değil **medyanın kendisine**
 * veriliyor ve kuyruk köşesi yöne göre düzleşiyor.
 */
export default function MessageVideo({ url, mine, durationSeconds, onLongPress }: Props) {
  const [playing, setPlaying] = useState(false);
  const ref = React.useRef<VideoRef>(null);
  const sure = formatVideoDuration(durationSeconds);
  const { t } = useTranslation();

  // Modül yoksa oynatılacak bir şey yok. Ölü bir oynat düğmesi çizmek yerine
  // ne yapılması gerektiği yazılıyor: kullanıcının elindeki tek çare güncelleme.
  if (!NativeVideo) {
    return (
      <TouchableOpacity
        testID="message-video"
        activeOpacity={0.9}
        onLongPress={onLongPress}>
        <View style={[styles.video, mine ? styles.videoMine : styles.videoOther, styles.fallback]}>
          <Text testID="message-video-unavailable" style={styles.fallbackText}>
            {t('message.videoNeedsUpdate')}
          </Text>
          {sure ? <Text testID="message-video-duration" style={styles.duration}>{sure}</Text> : null}
        </View>
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      testID="message-video"
      activeOpacity={0.9}
      onPress={() => {
        // Bittiğinde video SON karede duruyor; başa sarmadan oynatmak hiçbir
        // şey yapmıyordu. Simülatörde ikinci dokunuşun ölü olduğu görülerek
        // bulundu — birim testi "oynuyor mu" diye soruyordu, "yeniden
        // oynuyor mu" diye değil.
        ref.current?.seek(0);
        setPlaying(true);
      }}
      onLongPress={onLongPress}>
      <NativeVideo
        ref={ref}
        source={{ uri: url }}
        style={[styles.video, mine ? styles.videoMine : styles.videoOther]}
        resizeMode="cover"
        paused={!playing}
        controls={playing}
        // Oynatma bitince başa dönmüyor: kullanıcı isterse tekrar dokunur.
        // Döngü, veriyi sessizce tüketmenin bir başka yolu olurdu.
        repeat={false}
        onEnd={() => setPlaying(false)}
        testID="message-video-surface"
      />
      {!playing && (
        <View style={styles.overlay} pointerEvents="none">
          <View style={styles.playBadge}><Text style={styles.playGlyph}>▶</Text></View>
          {sure ? <Text testID="message-video-duration" style={styles.duration}>{sure}</Text> : null}
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  video: {
    width: MSG_VIDEO_WIDTH,
    height: MSG_VIDEO_HEIGHT,
    borderRadius: radius.card,
    backgroundColor: '#000',
  },
  fallback: { alignItems: 'center', justifyContent: 'center', paddingHorizontal: spacing.md },
  fallbackText: { color: '#fff', fontSize: 13, textAlign: 'center' },
  videoMine: { borderBottomRightRadius: 4 },
  videoOther: { borderBottomLeftRadius: 4 },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  playBadge: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playGlyph: { color: '#fff', fontSize: 20, marginLeft: 3 },
  duration: {
    position: 'absolute',
    right: spacing.sm,
    bottom: spacing.sm,
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
});
